import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChefHat, Loader2, Mail, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function signInWithGoogle() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      // Signal the Digital Corporate Chef to fire its "Welcome back, Boss."
      // pulse check on the dashboard. Cleared after the popup renders once.
      try { sessionStorage.setItem("cs:welcome-back-pending", "1"); } catch (_) { /* noop */ }
      toast.success("Welcome back, chef.");
      navigate("/dashboard", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      {/* LEFT: chef photo */}
      <div className="relative hidden md:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1551218808-94e220e084d2?auto=format&fit=crop&w=1600&q=80"
          alt="Chef preparing ingredients"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/50 to-black/70" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-foreground">
          <div className="label-eyebrow gold mb-4">Menu Costing • Est. 2026</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[1.02] mb-4 tracking-tight">
            Every gram.<br />Every drop.<br /><span className="gold">Every dollar.</span>
          </h1>
          <p className="text-foreground/70 max-w-md text-sm">
            A precision cost engine for chefs. Weight, volume, count, yield: reconciled to your bottom line, in the currency you actually earn in.
          </p>
        </div>
      </div>

      {/* RIGHT: glossy glass panel (body's fixed radial gradients refract through) */}
      <div className="relative flex items-center justify-center p-6 md:p-12">
        <div
          className="glass w-full max-w-sm rounded-[28px] p-8 md:p-10"
          data-testid="login-glass-panel"
        >
          {/* Brand */}
          <div className="flex items-center gap-3 mb-10">
            <img
              src="/brand/mark-256.png"
              alt="CulinaireSpecialist"
              className="w-10 h-10 rounded-full object-contain"
              style={{ background: "#0a0806", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15), 0 0 0 1px rgba(232,207,154,0.35), 0 4px 12px rgba(200,169,106,0.22)" }}
            />
            <div>
              <div className="font-display text-xl leading-tight text-white/95">CulinaireSpecialist</div>
              <div className="text-[0.55rem] uppercase tracking-[0.28em] mt-0.5" style={{ color: "rgba(232,207,154,0.85)" }}>
                Sign in
              </div>
            </div>
          </div>

          <h2 className="font-display text-3xl text-white/95 mb-2">Welcome back.</h2>
          <p className="text-sm text-white/55 mb-8">Sign in to your kitchen dashboard.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <button
              type="button"
              onClick={signInWithGoogle}
              className="glass-ghost w-full h-11 rounded-lg flex items-center justify-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors"
              data-testid="login-google"
            >
              <svg className="w-4 h-4" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-xs text-white/45">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-xs text-white/70">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/45 pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="glass-input w-full h-10 pl-9 pr-3 rounded-lg text-sm text-white/95"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-xs text-white/70">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/45 pointer-events-none" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="glass-input w-full h-10 pl-9 pr-3 rounded-lg text-sm text-white/95"
                  data-testid="login-password"
                />
              </div>
              <div className="pt-1 flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs text-white/60 hover:text-white/90 transition-colors"
                  data-testid="login-forgot-password"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="gold-cta group w-full h-11 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          <p className="text-sm text-white/50 mt-6">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              className="font-medium hover:brightness-110 transition-all"
              style={{ color: "#f2dfb5" }}
              data-testid="link-register"
            >
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
